import '../IntegrationsTab.scss'
import React, { useMemo, useState } from 'react'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'

import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { DynamicsSettingsModal } from './DynamicsSettingsModal'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { Link } from 'react-router-dom'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const ADMIN_CONSENT_LINK = import.meta.env.REACT_APP_MICROSOFT_ADMIN_CONSENT_LINK!

export type DynamicsIntegrationRowProps = {
  useIntegrationRedirectUri?: boolean
}
export const DynamicsIntegrationRow: React.FC<DynamicsIntegrationRowProps> = ({ useIntegrationRedirectUri }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const hasDynamicsIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.DYNAMICS))
  }, [userData])

  const handleShowSettingsModal = async () => {
    setShowSettingsModal(true)
  }

  const handleHideSettings = () => {
    setShowSettingsModal(false)
  }

  const authorizeDynamics = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.DYNAMICS)
    const state = useIntegrationRedirectUri
      ? createState(window.location.origin + '/dashboard/account/profile-settings/integrations', 'dynamics_discovery_step')
      : createState(null, 'dynamics_discovery_step')
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.DYNAMICS}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallDynamics = () => {
    const user = userData
    if (!user || !user.integrations) {
      return
    }
    const integration = user.integrations.find((i) => i.serviceId === IntegrationServiceId.DYNAMICS)
    if (integration) {
      deleteUserIntegration({
        accountId,
        payload: {
          userId: user.id,
          integrationId: integration.id,
        },
      })
    }
  }

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      {showSettingsModal && <DynamicsSettingsModal show={showSettingsModal} onHide={handleHideSettings} />}

      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.DYNAMICS} width={48} height={48} />
            </div>
            <div>
              <h6>Dynamics</h6>
              <div>
                Connect your Zocks account to Dynamics.
                <br />
                It might be needed for your admin to approve the integration. For more info, click{' '}
                <Link className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_CONSENT_LINK}>
                  here
                </Link>{' '}
                .
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {hasDynamicsIntegration && (
              <div className='d-flex gap-3'>
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'account:read', superScope: ['account:admin'] }}>
                  <Button variant='primaryGhost' onClick={handleShowSettingsModal}>
                    <i className='bi bi-gear' />
                  </Button>
                </ScopedBlock>
              </div>
            )}
            <div className='connect-button-container'>
              {hasDynamicsIntegration ? (
                <div className='connect-button-container'>
                  <Button variant='primary' onClick={uninstallDynamics} loading={isDeletingIntegration}>
                    Disconnect
                  </Button>
                  <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                    <i className='bi bi-check' />
                    Connected
                  </div>
                </div>
              ) : (
                <ScopedBlock
                  scope={{
                    scope: 'integration:connect',
                    superScope: ['integration:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button variant='primary' onClick={authorizeDynamics}>
                    Connect
                  </Button>
                </ScopedBlock>
              )}
            </div>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
